/**
 * Pure hreflang / localization path logic, shared by:
 *  - route `head()` (server-rendered tags, via buildRouteHead)
 *  - the root route shell (<html lang>) in src/routes/__root.tsx
 *
 * Keep this file free of React and browser APIs.
 */

export const SUPPORTED_LANGUAGES = [
  { code: "en", hreflang: "en" },
  { code: "zh-cn", hreflang: "zh" },
  { code: "es", hreflang: "es" },
] as const;

/** Known language prefixes used in routes */
export const LANG_PREFIXES = ["en", "es", "zh-cn"];

/** Maps route prefix to BCP 47 lang code for the <html lang> attribute */
export const HTML_LANG_MAP: Record<string, string> = {
  en: "en",
  es: "es",
  "zh-cn": "zh-Hans",
};

/**
 * Bidirectional path mapping between EN and ES localized routes.
 * Only for paths that ACTUALLY differ between languages in the router.
 */
export const ES_PATH_MAP: Record<string, string> = {
  // Industries
  "/industries": "/industrias",
  // About
  "/about-qbd": "/sobre-qbd",
  "/about-qbd/ecosystem": "/sobre-qbd/ecosistema",
  "/about-qbd/story": "/sobre-qbd/historia",
  "/about-qbd/team": "/sobre-qbd/equipo",
  "/about-qbd/board-of-advisors": "/sobre-qbd/consejo-asesor",
  "/about-qbd/qbd-group-foundation": "/sobre-qbd/fundacion",
  // Services
  "/services/regulatory-toxicology/pde-oel-reports": "/servicios/toxicologia-regulatoria/informes-pde-oel",
  "/services/regulatory-toxicology/nitrosamine-risk-assessment": "/servicios/toxicologia-regulatoria/evaluacion-de-riesgos-de-las-nitrosaminas",
  "/services/regulatory-toxicology/extractables-leachables-reports": "/servicios/toxicologia-regulatoria/informes-sobre-extractables-y-lixiviables",
  "/services/regulatory-toxicology/environmental-risk-assessment-era": "/servicios/toxicologia-regulatoria/evaluacion-de-riesgos-medioambientales-era",
  "/services/regulatory-toxicology/elemental-impurities-reports": "/servicios/toxicologia-regulatoria/informes-sobre-impurezas-elementales",
  "/services/regulatory-toxicology/organic-impurities-reports": "/servicios/toxicologia-regulatoria/informes-sobre-impurezas-organicas",
  "/services/regulatory-toxicology/residual-solvents-reports": "/servicios/toxicologia-regulatoria/informes-sobre-disolventes-residuales",
  "/services/regulatory-toxicology/reach-registration-compliance-support": "/servicios/toxicologia-regulatoria/registro-reach-y-apoyo-al-cumplimiento",
  "/services/regulatory-toxicology/monitoring-studies": "/servicios/toxicologia-regulatoria/estudios-de-seguimiento",
  "/services/regulatory-toxicology": "/servicios/toxicologia-regulatoria",
  "/services": "/servicios",
  // Solutions
  "/solutions": "/soluciones",
  // Contact
  "/contact": "/contacto",
  // Content sections
  "/events": "/eventos",
  "/news": "/noticias",
  "/regulatory-updates": "/actualizaciones-regulatorias",
  "/blog": "/blog",
  "/knowledge-center": "/recursos",
  "/annex-1-resource-center": "/centro-recursos-anexo-1",
  "/knowledge-center/glossary": "/recursos/glosario",
  "/case-studies": "/casos-de-exito",
  "/request-your-audit": "/solicita-tu-auditoria",
  "/newsletter": "/suscribete",
  "/certifications": "/certificaciones",
  // Legal
  "/cookie-statement": "/declaracion-de-cookies",
  "/privacy-policy": "/politica-de-privacidad",
  "/terms-and-conditions": "/terminos-y-condiciones",
  "/supplier-code-of-conduct": "/codigo-de-conducta-del-proveedor",
};

const sortedEnToEs = Object.entries(ES_PATH_MAP).sort(([a], [b]) => b.length - a.length);

const sortedEsToEn: [string, string][] = Object.entries(ES_PATH_MAP)
  .map(([en, es]) => [es, en] as [string, string])
  .sort(([a], [b]) => b.length - a.length);

/**
 * ZH-CN route patterns that actually exist in the router.
 * Only emit ZH-CN hreflang for pages matching these patterns.
 */
const ZH_CN_PATTERNS: RegExp[] = [
  /^$/, // homepage
  /^\/blog(\/|$)/,
  /^\/industries(\/|$)/,
  /^\/solutions(\/|$)/,
  /^\/services(\/[^/]+)?$/,
  /^\/services\/regulatory-toxicology\/(?!biocompatibility-biological-evaluation$)[^/]+$/,
  /^\/certifications$/,
  /^\/about-qbd(\/story|\/team|\/ecosystem)?$/,
  /^\/contact$/,
  /^\/news(\/|$)/,
  /^\/webinars(\/|$)/,
  /^\/downloads(\/|$)/,
  /^\/case-studies(\/|$)/,
  /^\/regulatory-updates(\/|$)/,
  /^\/author\//,
  /^\/supplier-code-of-conduct$/,
  /^\/newsletter$/,
  /^\/knowledge-center\/glossary(\/|$)/,
];

const hasZhCnRoute = (enPathRemainder: string): boolean =>
  ZH_CN_PATTERNS.some((re) => re.test(enPathRemainder));

export const normalizePath = (pathname: string): string =>
  pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";

export const getLangFromPath = (pathname: string): string => {
  const normPath = normalizePath(pathname);
  for (const prefix of LANG_PREFIXES) {
    if (normPath === `/${prefix}` || normPath.startsWith(`/${prefix}/`)) return prefix;
  }
  return "en";
};

export const getHtmlLang = (pathname: string): string =>
  HTML_LANG_MAP[getLangFromPath(pathname)] || "en";

/** Strip the language prefix, returning the path remainder ("" for a lang home) */
export const getPathWithoutLang = (pathname: string): string => {
  const normPath = normalizePath(pathname);
  for (const prefix of LANG_PREFIXES) {
    if (normPath === `/${prefix}`) return "";
    if (normPath.startsWith(`/${prefix}/`)) return normPath.slice(prefix.length + 1);
  }
  return normPath === "/" ? "" : normPath;
};

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

/**
 * Build the hreflang alternate set for a pathname.
 * `siteUrl` must be an absolute origin without a trailing slash.
 */
export function buildHreflangAlternates(pathname: string, siteUrl: string): HreflangAlternate[] {
  const currentLang = getLangFromPath(pathname);
  const pathRemainder = getPathWithoutLang(pathname);

  const toEnglish = (remainder: string): string => {
    if (currentLang !== "es") return remainder;
    for (const [es, en] of sortedEsToEn) {
      if (remainder === es || remainder.startsWith(es + "/")) {
        return en + remainder.slice(es.length);
      }
    }
    return remainder;
  };

  const localizePath = (targetLang: string): string => {
    if (targetLang === currentLang) return pathRemainder;
    const enPath = toEnglish(pathRemainder);
    if (targetLang === "es") {
      for (const [en, es] of sortedEnToEs) {
        if (enPath === en || enPath.startsWith(en + "/")) {
          return es + enPath.slice(en.length);
        }
      }
    }
    // ZH-CN and EN share the same path structure
    return enPath;
  };

  // Article/detail pages use different slugs per language, so a path-swapped
  // alternate usually 404s (a hreflang conflict). Emit only the self reference.
  if (/^\/(blog|news|regulatory-updates|case-studies|webinars|events|downloads|white-papers)\/.+/.test(toEnglish(pathRemainder))) {
    const self = SUPPORTED_LANGUAGES.find(({ code }) => code === currentLang) ?? SUPPORTED_LANGUAGES[0];
    return [{ hreflang: self.hreflang, href: `${siteUrl}/${currentLang}${pathRemainder}` }];
  }

  const buildUrl = (langCode: string) => `${siteUrl}/${langCode}${localizePath(langCode)}`;
  const includeZhCn = hasZhCnRoute(toEnglish(pathRemainder));

  const alternates: HreflangAlternate[] = SUPPORTED_LANGUAGES.filter(
    ({ code }) => code !== "zh-cn" || includeZhCn
  ).map(({ code, hreflang }) => ({ hreflang, href: buildUrl(code) }));

  alternates.push({ hreflang: "x-default", href: buildUrl("en") });
  return alternates;
}
